/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Address } from '@vsolv/core/address/domain';
import { Config } from '@vsolv/packages/portal-config/domain';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { debouncedSearch } from '@vsolv/utils/rxjs-operators';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { CurrencyMaskConfig } from '@vsolv/vectors-ui/input';
import { Distributor } from '@wsphere/distributors/domain';
import { DistributorService, UnitPricePlanRow } from '@wsphere/distributors/web';
import { SecurityService } from '@wsphere/staff/web';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  firstValueFrom,
  from,
  map,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EditClaimsMessageDialog, EditPromoTextDialog } from '../../dialogs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-web-organization-page',
  templateUrl: './organization.page.html',
  standalone: false,
})
export class OrganizationPage implements OnInit, OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private portalSvc: PortalService,
    private formBuilder: FormBuilder,
    private securitySvc: SecurityService,
    private distributorSvc: DistributorService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  @ViewChild('promoTextDialog') promoTextDialog?: EditPromoTextDialog;
  @ViewChild('claimsMessageDialog') claimsMessageDialog?: EditClaimsMessageDialog;

  private onDestroy$ = new Subject<void>();
  private isDestroyed = false;
  private activePlansLoadToken = 0;
  private readonly unitPriceSearchInput$ = new Subject<string>();
  private unitPriceFormChangesDestroy$ = new Subject<void>();

  readonly currencyMaskConfig: CurrencyMaskConfig = {
    align: 'left',
    allowNegative: false,
    allowZero: false,
    decimal: '.',
    nullable: false,
    precision: 2,
    prefix: '$',
    suffix: '',
    thousands: ',',
    inputMode: 1,
  };

  protected distiSettings?: Distributor.RetrieveDistributorsSettingsResponse | null;
  private refresh$ = new BehaviorSubject<null>(null);
  staff$ = this.route.parent!.parent!.parent!.data.pipe(map(data => data['staff']));
  private readonly globalDistributors$ = this.securitySvc.globalDistributors$;

  config$ = this.refresh$.pipe(
    switchMap(async () => {
      const configData = this.portalSvc.getConfigData;
      this.portalConfigDetail = configData;
      this.resetForm();
      return configData;
    })
  );

  data$ = combineLatest([this.config$, this.staff$, this.refresh$]).pipe(
    map(([config, staff, refresh]) => ({
      config,
      staff,
      refresh,
    }))
  );

  canEditOrganization$ = this.globalDistributors$.pipe(
    switchMap(async globalDist => {
      return this.securitySvc.hasAccess('stf_EditSettings', globalDist?.map(dist => dist.permissionKey) || null);
    }),
    tap(canEdit => {
      canEdit ? this.form.enable() : this.form.disable();
      canEdit ? this.distributorForm.enable() : this.distributorForm.disable();
    })
  );

  distributorSaving = false;
  portalSaving = false;

  portalConfigDetail!: Config.PortalConfig;
  resetFormValue = {
    brandName: '',
    tagLine: '',
    productName: '',
    contactEmail: '',
    contactPhone: '',
    contactAddress: <Address.Model | null>null,
    fromName: '',
    fromEmail: '',
    replyToName: '',
    replyToEmail: '',
    productTeam: '',
  };

  form = this.formBuilder.group({
    brandName: new FormControl({ value: '', disabled: false }, Validators.required),
    tagLine: new FormControl({ value: '', disabled: false }, Validators.required),
    productName: new FormControl({ value: '', disabled: false }, Validators.required),
    contactEmail: new FormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
    contactPhone: new FormControl({ value: '', disabled: false }, [Validators.required]),
    contactAddress: new FormControl({ value: <Address.Model | null>null, disabled: false }, [Validators.required]),
    fromName: new FormControl({ value: '', disabled: false }, [Validators.required]),
    fromEmail: new FormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
    replyToName: new FormControl({ value: '', disabled: false }, [Validators.required]),
    replyToEmail: new FormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
    productTeam: new FormControl({ value: '', disabled: false }, [Validators.required]),
  });

  private unitPriceControlIndexByPlanId = new Map<string, number>();
  unitPriceRows: UnitPricePlanRow[] = [];
  unitPriceTotalItems = 0;
  unitPricePage = 1;
  unitPriceLimit = 5;
  unitPriceSearch = '';

  distributorForm = this.formBuilder.group({
    userRegistration: new FormControl({ value: false, disabled: false }),
    selfServeInvoicePackage: this.formBuilder.group({
      enabled: [false as boolean, Validators.required],
      logo: [null as File | null],
      companyName: [null as string | null, Validators.required],
      companyAddress: [null as string | null, Validators.required],
      bankingDetails: this.formBuilder.group({
        bank: [null as string | null],
        accountNumber: [null as string | null, [Validators.pattern(/\d+/)]],
        routingNumber: [null as string | null, [Validators.pattern(/\d+/)]],
      }),
      unitPrices: this.formBuilder.array<
        FormGroup<{
          plan: FormControl<string>;
          price: FormControl<number | null>;
        }>
      >([]),
    }),
  });

  portalForm = this.formBuilder.group({
    loginDisabled: new FormControl({ value: false, disabled: false }),
    salesDisabled: new FormControl({ value: false, disabled: false }),
    promoText: new FormControl({ value: '', disabled: false }),
    claimsMessage: new FormControl({ value: '', disabled: false }),
  });

  promoTextForm = this.formBuilder.group({
    promoText: [''],
  });

  resetFormValues() {
    this.resetFormValue = {
      brandName: this.portalConfigDetail.brandName || '',
      productName: this.portalConfigDetail.productName || '',
      tagLine: this.portalConfigDetail.tagLine || '',
      contactEmail: this.portalConfigDetail.contactEmail || '',
      contactPhone: this.portalConfigDetail.contactPhone || '',
      contactAddress: this.portalConfigDetail.contactAddress || null,
      fromName: this.portalConfigDetail.fromName || '',
      fromEmail: this.portalConfigDetail.fromEmail || '',
      replyToName: this.portalConfigDetail.replyToName || '',
      replyToEmail: this.portalConfigDetail.replyToEmail || '',
      productTeam: this.portalConfigDetail.productTeam || '',
    };
  }

  inputIsValid() {
    return this.form.valid;
  }

  async resetForm() {
    try {
      this.resetFormValues();
      this.form.reset(this.resetFormValue);
      this.form.markAsPristine();
    } catch (e) {
      console.error(e);
    }
  }

  async confirm() {
    this.form.disable();
    try {
      await this.updateOrgDetails();
      this.resetForm();
      this.toastSvc.show({
        type: 'success',
        title: 'Organization settings updated',
        text: 'Your administration portal settings have been successfully updated.',
      });
    } catch (e) {
      this.toastSvc.show({
        type: 'success',
        title: 'Error',
        text: `${e}`,
      });
    }
    this.form.enable();
  }

  async saveDistributor() {
    if (
      !this.distributorForm.dirty ||
      (this.distributorForm.value.selfServeInvoicePackage?.enabled &&
        this.distributorForm.controls['selfServeInvoicePackage'].invalid)
    ) {
      return;
    }
    this.distributorSaving = true;

    // Ensure we flush any in-progress unit price edits before saving.
    if (this.distributorForm.value.selfServeInvoicePackage?.enabled) {
      this.syncUnitPricesFromFormArray();
    }

    const value = this.distributorForm.value;

    const toBase64 = (file: File) => {
      return new Promise<string | null>(res => {
        const reader = new FileReader();

        reader.onload = () => {
          const img = document.createElement('img');

          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = (img.width * 64) / img.height;
            canvas.height = 64;

            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            const dataUrl = canvas.toDataURL(file.type);

            canvas.remove();
            img.remove();

            res(dataUrl);
          };

          img.src = reader.result as string;
        };

        reader.readAsDataURL(file);
      });
    };

    const logoFile = this.distributorForm.value.selfServeInvoicePackage?.logo;
    const logoUrl = logoFile ? await toBase64(logoFile) : null;

    const updated = await this.distributorSvc
      .updateSettings({
        canRegisterUsers: !!value.userRegistration,
        selfServeInvoicePackage: {
          enabled: this.distributorForm.value.selfServeInvoicePackage?.enabled ?? false,

          logo: logoUrl,
          companyName: this.distributorForm.value.selfServeInvoicePackage?.companyName,
          companyAddress: this.distributorForm.value.selfServeInvoicePackage?.companyAddress,

          bankingDetails: {
            bank: this.distributorForm.value.selfServeInvoicePackage?.bankingDetails?.bank ?? null,
            accountNumber: this.distributorForm.value.selfServeInvoicePackage?.bankingDetails?.accountNumber ?? null,
            routingNumber: this.distributorForm.value.selfServeInvoicePackage?.bankingDetails?.routingNumber ?? null,
          },

          unitPrices: (this.distiSettings?.selfServeInvoicePackage?.unitPrices ?? []).map(p => ({
            plan: p.plan,
            price: p.price,
          })),
        },
      })
      .catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });

        this.distributorSaving = false;
      });

    if (updated) {
      this.toastSvc.show({
        type: 'success',
        title: 'Distributor Settings Updated',
        text: 'Successfully updated distributor settings for your organization.',
      });

      this.distributorForm.markAsUntouched();
      this.distributorForm.reset(this.distributorForm.value);

      this.distributorForm.updateValueAndValidity();

      this.distributorSaving = false;
    }
  }

  async savePortal() {
    if (!this.portalForm.dirty) {
      return;
    }
    this.portalSaving = true;

    try {
      // Get the values from the form
      const loginDisabled = !!this.portalForm.value.loginDisabled;
      const salesDisabled = !!this.portalForm.value.salesDisabled;
      const promoText = this.portalForm.value.promoText || '';
      const claimsMessage = this.portalForm.value.claimsMessage || '';

      // Save the settings using the portal service
      const updated = await this.portalSvc.updateConfig({
        loginDisabled,
        salesDisabled,
        promoText,
        claimsMessage,
      });

      if (updated) {
        this.toastSvc.show({
          type: 'success',
          title: 'Portal Settings Updated',
          text: 'Successfully updated customer portal settings for your organization.',
        });

        this.portalForm.markAsPristine();
        this.portalForm.reset({ loginDisabled, salesDisabled, promoText, claimsMessage }); // reset to current values
        this.portalForm.updateValueAndValidity();
      }
    } catch (error: unknown) {
      const err = error as { message?: string };
      const message = err?.message ?? (String(error) || 'Failed to update portal settings.');
      this.toastSvc.show({
        type: 'error',
        title: 'Error',
        text: message,
      });
    } finally {
      this.portalSaving = false;
    }
  }

  resetPortalForm() {
    // Reset the form to the original values from portalConfigDetail
    this.portalForm.reset({
      loginDisabled: !!this.portalConfigDetail.loginDisabled,
      salesDisabled: !!this.portalConfigDetail.salesDisabled,
      promoText: this.portalConfigDetail.promoText,
      claimsMessage: this.portalConfigDetail.claimsMessage,
    });
    this.portalForm.markAsPristine();
  }

  async updateOrgDetails() {
    const formControlValue = this.form.getRawValue();

    const updatedPortalConfig: Config.UpdatePortalConfigDetailsRequestDto = {};

    if (formControlValue.brandName !== this.resetFormValue.brandName) {
      updatedPortalConfig.brandName = formControlValue.brandName || '';
    }

    if (formControlValue.tagLine !== this.resetFormValue.tagLine) {
      updatedPortalConfig.tagLine = formControlValue.tagLine || '';
    }

    if (formControlValue.productName !== this.resetFormValue.productName) {
      updatedPortalConfig.productName = formControlValue.productName || '';
    }

    if (formControlValue.contactEmail !== this.resetFormValue.contactEmail) {
      updatedPortalConfig.contactEmail = formControlValue.contactEmail || '';
    }

    if (formControlValue.contactPhone !== this.resetFormValue.contactPhone) {
      updatedPortalConfig.contactPhone = formControlValue.contactPhone || '';
    }

    if (formControlValue.contactAddress !== this.resetFormValue.contactAddress) {
      updatedPortalConfig.contactAddress = formControlValue.contactAddress || null;
    }

    if (formControlValue.fromName !== this.resetFormValue.fromName) {
      updatedPortalConfig.fromName = formControlValue.fromName || '';
    }

    if (formControlValue.fromEmail !== this.resetFormValue.fromEmail) {
      updatedPortalConfig.fromEmail = formControlValue.fromEmail || '';
    }

    if (formControlValue.replyToName !== this.resetFormValue.replyToName) {
      updatedPortalConfig.replyToName = formControlValue.replyToName || '';
    }

    if (formControlValue.replyToEmail !== this.resetFormValue.replyToEmail) {
      updatedPortalConfig.replyToEmail = formControlValue.replyToEmail || '';
    }

    if (formControlValue.productTeam !== this.resetFormValue.productTeam) {
      updatedPortalConfig.productTeam = formControlValue.productTeam || '';
    }

    const result = (await this.portalSvc.updateConfig(updatedPortalConfig)) as { config: Config.PortalConfig };
    this.portalConfigDetail = result.config;
  }

  refreshPage() {
    this.refresh$.next(null);
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.activePlansLoadToken++;
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  async ngOnInit() {
    this.toggleSelfServe(false);

    this.distiSettings =
      (await this.distributorSvc.getSettings()) as Distributor.RetrieveDistributorsSettingsResponse | null;
    const hasAccess = await this.securitySvc.hasAccess('stf_EditSettings', null);

    if (this.distiSettings) {
      this.distributorForm.disable();

      this.distributorForm.controls.userRegistration.reset(this.distiSettings.canRegisterUsers ?? null);

      this.distributorForm.controls.selfServeInvoicePackage.reset({
        enabled: this.distiSettings.selfServeInvoicePackage?.enabled ?? false,

        logo: null,
        companyName: this.distiSettings.selfServeInvoicePackage?.companyName ?? null,
        companyAddress: this.distiSettings.selfServeInvoicePackage?.companyAddress ?? null,

        bankingDetails: {
          bank: this.distiSettings.selfServeInvoicePackage?.bankingDetails?.bank ?? null,
          accountNumber: this.distiSettings.selfServeInvoicePackage?.bankingDetails?.accountNumber ?? null,
          routingNumber: this.distiSettings.selfServeInvoicePackage?.bankingDetails?.routingNumber ?? null,
        },

        unitPrices: [],
      });

      if (hasAccess) this.distributorForm.enable();

      const selfServeEnabled = this.distributorForm.controls.selfServeInvoicePackage.controls.enabled.value ?? false;

      this.distributorForm.controls.selfServeInvoicePackage.controls.enabled.valueChanges
        .pipe(
          startWith(selfServeEnabled),
          distinctUntilChanged(),
          tap(enabled => this.toggleSelfServe(!!enabled)),
          switchMap(async enabled => {
            if (!enabled) {
              // Preserve any in-progress (unsaved) edits before we hide/disable the section.
              this.syncUnitPricesFromFormArray();
              this.unitPriceRows = [];
              this.unitPriceTotalItems = 0;
              this.unitPricePage = 1;
              return;
            }

            // load initial page for unit price editor
            await this.loadUnitPricePlanPage();
            return;
          }),
          takeUntil(this.onDestroy$)
        )
        .subscribe();

      this.unitPriceSearchInput$
        .pipe(
          debouncedSearch(300),
          tap(search => {
            this.unitPriceSearch = search;
            this.unitPricePage = 1;
          }),
          switchMap(() => from(this.loadUnitPricePlanPage())),
          catchError(() => EMPTY),
          takeUntil(this.onDestroy$)
        )
        .subscribe();
    }

    this.portalForm.reset({
      loginDisabled: !!this.portalConfigDetail.loginDisabled,
      salesDisabled: !!this.portalConfigDetail.salesDisabled,
      promoText: this.portalConfigDetail.promoText,
      claimsMessage: this.portalConfigDetail.claimsMessage,
    });
  }

  // NOTE: We intentionally do NOT load all policies/plans anymore.
  // Unit prices are edited via the paginated/search endpoint.

  async loadUnitPricePlanPage(): Promise<void> {
    if (!this.distiSettings) return;
    const loadToken = ++this.activePlansLoadToken;

    const response = await firstValueFrom(
      this.distributorSvc.listUnitPricePlans$({
        page: this.unitPricePage,
        limit: this.unitPriceLimit,
        search: this.unitPriceSearch?.trim() || undefined,
      })
    );

    if (this.isDestroyed || loadToken !== this.activePlansLoadToken) return;

    this.unitPriceRows = response.items;
    this.unitPriceTotalItems = response.meta.totalItems ?? 0;

    // Prefer in-memory edits (not-yet-saved) over the server-returned unitPrice.
    const localUnitPriceByPlanId = new Map<string, number | null>(
      (this.distiSettings.selfServeInvoicePackage?.unitPrices ?? []).map(p => [p.plan, p.price ?? null])
    );

    const array = this.distributorForm.controls.selfServeInvoicePackage.controls.unitPrices;
    array.clear();
    this.unitPriceControlIndexByPlanId.clear();
    this.unitPriceFormChangesDestroy$.next();
    this.unitPriceFormChangesDestroy$.complete();
    this.unitPriceFormChangesDestroy$ = new Subject<void>();

    response.items.forEach((row, idx) => {
      this.unitPriceControlIndexByPlanId.set(row.planId, idx);
      const effectiveUnitPriceCents =
        localUnitPriceByPlanId.get(row.planId) ?? (row.unitPrice === undefined ? null : row.unitPrice);
      array.insert(
        idx,
        this.formBuilder.group({
          plan: [row.planId as string, Validators.required],
          price: [
            (effectiveUnitPriceCents === null || effectiveUnitPriceCents === undefined
              ? null
              : effectiveUnitPriceCents / 100) as number | null,
            [Validators.min(0)],
          ],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }) as any
      );
    });

    // Ensure the form array is editable when self-serve is enabled.
    // (If the parent group was disabled earlier, newly inserted controls can remain disabled.)
    if (this.distributorForm.value.selfServeInvoicePackage?.enabled) {
      array.enable({ emitEvent: false });
    }

    // Mark the overall distributor form dirty as soon as any unit price changes.
    // Also keep distiSettings.unitPrices in sync so the Save payload matches what users edited.
    array.valueChanges
      .pipe(
        debounceTime(150),
        tap(() => this.syncUnitPricesFromFormArray()),
        takeUntil(this.unitPriceFormChangesDestroy$),
        takeUntil(this.onDestroy$)
      )
      .subscribe();

    this.cdr.detectChanges();
  }

  async onUnitPricePaginationChange(event: { currentPage: number; itemsPerPage: number }) {
    this.unitPricePage = event.currentPage;
    this.unitPriceLimit = event.itemsPerPage;
    await this.loadUnitPricePlanPage();
  }

  onUnitPriceSearchChange(value: string) {
    this.unitPriceSearchInput$.next(value);
  }

  onUnitPriceBlur(_planId: string) {
    // Ensure we capture edits even if users tab out quickly.
    this.syncUnitPricesFromFormArray();
  }

  private syncUnitPricesFromFormArray(): void {
    if (!this.distiSettings?.selfServeInvoicePackage) return;

    const array = this.distributorForm.controls.selfServeInvoicePackage.controls.unitPrices;
    const values = array.getRawValue() as Array<{ plan: string; price: number | null }>;

    const existing = this.distiSettings.selfServeInvoicePackage.unitPrices ?? [];
    const map = new Map(existing.map(p => [p.plan, p.price ?? null]));

    for (const row of values) {
      if (!row?.plan) continue;
      const priceCents = row.price === null || row.price === undefined ? null : Math.round(row.price * 100);
      map.set(row.plan, priceCents);
    }

    this.distiSettings.selfServeInvoicePackage.unitPrices = Array.from(map.entries()).map(([plan, price]) => ({
      plan,
      price,
    }));

    // This drives the Save button visibility (`*ngIf="distributorForm.dirty"`).
    this.distributorForm.markAsDirty();
  }

  getControlIndex(planId: string) {
    const index = this.unitPriceControlIndexByPlanId.get(planId);
    return index !== undefined ? { index } : null;
  }

  private toggleSelfServe(enabled: boolean) {
    Object.entries(this.distributorForm.controls.selfServeInvoicePackage.controls)
      .filter(([key]) => key !== 'enabled')
      .map(([_, control]) => control)
      .forEach(control => {
        if (enabled) control.enable();
        else control.disable();
      });
  }

  openPromoTextDialog() {
    // Always set the dialog's value to the current in-memory value before opening
    if (this.promoTextDialog) {
      this.promoTextDialog.value = this.portalForm.value.promoText || '';
      this.promoTextDialog.openDialog();
    }
  }

  onPromoTextKeydown(event: KeyboardEvent) {
    // Allow navigation keys
    const allowedKeys = [
      'Tab',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'PageUp',
      'PageDown',
    ];

    // Open modal on Enter or Space
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openPromoTextDialog();
      return;
    }

    // Allow navigation keys
    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow Ctrl/Cmd + A (select all), Ctrl/Cmd + C (copy)
    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'A', 'C'].includes(event.key)) {
      return;
    }

    // Prevent all other input
    event.preventDefault();
  }

  onPromoTextSaved(newText: string) {
    this.portalForm.patchValue({ promoText: newText });
    this.portalForm.markAsDirty();
  }

  openClaimsMessageDialog() {
    if (this.claimsMessageDialog) {
      this.claimsMessageDialog.value = this.portalForm.value.claimsMessage || '';
      this.claimsMessageDialog.openDialog();
    }
  }

  onClaimsMessageKeydown(event: KeyboardEvent) {
    const allowedKeys = [
      'Tab',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'PageUp',
      'PageDown',
    ];

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openClaimsMessageDialog();
      return;
    }

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'A', 'C'].includes(event.key)) {
      return;
    }

    event.preventDefault();
  }

  onClaimsMessageSaved(newText: string) {
    this.portalForm.patchValue({ claimsMessage: newText });
    this.portalForm.markAsDirty();
  }

  get claimsMessagePlain(): string {
    const claimsMessage = this.portalForm.value.claimsMessage;
    if (!claimsMessage || claimsMessage.trim().length === 0) {
      return '';
    }
    const div = document.createElement('div');
    div.innerHTML = claimsMessage;
    return div.textContent || div.innerText || '';
  }

  savePromoText() {
    this.portalForm.patchValue({ promoText: this.promoTextForm.value.promoText || '' });
    this.portalForm.markAsDirty();
    this.promoTextDialog?.closeDialog();
  }

  get sanitizedPromoText(): SafeHtml {
    const promoText = this.portalForm.value.promoText;
    return this.sanitizer.bypassSecurityTrustHtml(
      promoText && promoText.trim().length > 0
        ? promoText
        : `<span class='text-gray-400'>Click to add promo text...</span>`
    );
  }

  get promoTextPlain(): string {
    const promoText = this.portalForm.value.promoText;
    if (!promoText || promoText.trim().length === 0) {
      return '';
    }
    // Strip HTML tags for plain text display
    const div = document.createElement('div');
    div.innerHTML = promoText;
    return div.textContent || div.innerText || '';
  }
}
